import React, { useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import TenderRequestTable from './components/TenderRequestTable';
import TenderRequestForm from './components/TenderRequestForm';
import TenderStatusChangeDialog from './components/TenderStatusChangeDialog';
import TenderCommentHistoryDialog from './components/TenderCommentHistoryDialog';
import useTenderRequestData from './hooks/useTenderRequestData';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useLmsPermission } from '../hooks/useLmsPermission';
import { LMS } from '../../../constants/permissionCodes';
import { useGetLmsLookupTranslationsQuery } from '../../../api';
import { formatErrorMessage } from '../../../utils/errorUtils';

/**
 * İhale Talebi (LmsTenderRequest) sayfası — bkz. docs/LMS-IHALE--FE-Dokumani.txt. Kullanıcı
 * isteği: sekmeli yapıya gerek yok, listeleme tek ekranda yapılıyor (training-request/'daki
 * sekmeli yapının aksine). Onay akışı (statü geçiş/yorum geçmişi) "Tüm Talepler" sekmesiyle
 * aynı desende, requestType: 3 ile ortak uçtan.
 */
const TenderRequestPage = () => {
    const { th } = useLanguage();
    const { can } = useLmsPermission();
    const [selectedItem, setSelectedItem] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [statusChangeContext, setStatusChangeContext] = useState(null);
    const [statusChangeLoading, setStatusChangeLoading] = useState(false);
    const [historyRequestId, setHistoryRequestId] = useState(null);
    const toast = useRef(null);

    const {
        tenderRequestApi,
        isLoading,
        lazyParams,
        onPage,
        saveRequest,
        activeFilters,
        onFiltersChange,
        initFilters,
        sortField,
        sortOrder,
        onSortChange,
        deleteRequest,
        changeStatus,
        refetch
    } = useTenderRequestData();

    const { data: vehicleModelsData } = useGetLmsLookupTranslationsQuery({ groupCode: 'VEHICLE_MODEL', isActive: 1, pageNumber: 1, pageSize: 100 });
    const { data: tenderRequestTypesData } = useGetLmsLookupTranslationsQuery({ groupCode: 'TENDER_REQUEST_TYPE', isActive: 1, pageNumber: 1, pageSize: 100 });

    const vehicleModelOptions = useMemo(() => (vehicleModelsData?.items || []).map((item) => ({ value: item.code, label: item.text })), [vehicleModelsData]);
    const tenderRequestTypeOptions = useMemo(() => (tenderRequestTypesData?.items || []).map((item) => ({ value: item.code, label: item.text })), [tenderRequestTypesData]);

    const handleEdit = (rowData) => {
        setSelectedItem(rowData);
        setDialogVisible(true);
    };

    const handleCreateClick = () => {
        setSelectedItem(null);
        setDialogVisible(true);
    };

    const handleHide = () => {
        setDialogVisible(false);
        setSelectedItem(null);
    };

    const handleSubmit = async (formData) => {
        const success = await saveRequest(formData);
        if (success) {
            handleHide();
            refetch?.();
        }
    };

    const handleDelete = (id) => {
        if (!id) return;
        confirmDialog({
            message: th('tenderRequest.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                handleHide();
                try {
                    await deleteRequest(id);
                    toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('tenderRequest.deleteSuccess'), life: 3000 });
                    refetch?.();
                } catch {
                    toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('tenderRequest.deleteError'), life: 4000 });
                }
            }
        });
    };

    const handleStatusAction = (row, targetStatus) => setStatusChangeContext({ row, targetStatus });
    const handleStatusChangeHide = () => {
        if (statusChangeLoading) return;
        setStatusChangeContext(null);
    };

    const handleStatusChangeConfirm = async (comment) => {
        if (!statusChangeContext) return;
        const { row, targetStatus } = statusChangeContext;
        setStatusChangeLoading(true);
        try {
            await changeStatus({ id: row.requestId ?? row.id, newStatus: targetStatus, comment });
            toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('tenderRequest.statusChange.success'), life: 3000 });
            setStatusChangeContext(null);
            refetch?.();
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: formatErrorMessage(err) || th('tenderRequest.statusChange.error'), life: 4000 });
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const handleShowHistory = (row) => setHistoryRequestId(row.requestId ?? row.id ?? null);
    const handleHideHistory = () => setHistoryRequestId(null);

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('tenderRequest.title')}</h1>
                <div className="flex gap-2">
                    <Button label={th('filter.reset')} icon="pi pi-filter-slash" outlined onClick={initFilters} />
                    {can(LMS.REQUEST_CREATE) && <Button label={th('tenderRequest.newButton')} icon="pi pi-plus" onClick={handleCreateClick} />}
                </div>
            </div>

            <TenderRequestTable
                data={tenderRequestApi}
                totalCount={tenderRequestApi?.totalCount}
                loading={isLoading}
                lazyParams={lazyParams}
                onPage={onPage}
                onEdit={handleEdit}
                activeFilters={activeFilters}
                onFiltersChange={onFiltersChange}
                vehicleModelOptions={vehicleModelOptions}
                tenderRequestTypeOptions={tenderRequestTypeOptions}
                sortField={sortField}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
                onShowHistory={handleShowHistory}
            />

            {dialogVisible && (
                <TenderRequestForm
                    visible={dialogVisible}
                    onHide={handleHide}
                    onSubmit={handleSubmit}
                    initialData={selectedItem}
                    loading={isLoading}
                    handleDelete={can(LMS.REQUEST_DELETE) ? handleDelete : undefined}
                    readOnly={!!selectedItem && !can(LMS.REQUEST_UPDATE)}
                    // Onay/red ile güncelleme ayrımı iş birimi kararını bekliyor; BE ikisinden birini kabul ediyor
                    onStatusAction={can([LMS.REQUEST_UPDATE, LMS.REQUEST_APPROVE]) ? handleStatusAction : undefined}
                />
            )}

            {statusChangeContext && (
                <TenderStatusChangeDialog visible={!!statusChangeContext} targetStatus={statusChangeContext.targetStatus} onHide={handleStatusChangeHide} onConfirm={handleStatusChangeConfirm} loading={statusChangeLoading} />
            )}

            {historyRequestId != null && <TenderCommentHistoryDialog visible={historyRequestId != null} requestId={historyRequestId} onHide={handleHideHistory} />}
        </div>
    );
};

export default TenderRequestPage;
